import React from 'react';
import { Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import Seo from '../../../components/Seo';
import routes from '../../../setup/consts/routes';
import { FormInput } from '../../../components/atoms/Fields/Input';
import SandoogLogo from '../../../components/atoms/SandoogLogo';
import { ButtonKind, ButtonSize } from '../../../components/atoms/Button/enums';
import { SIGN_IN_FORM_INITIAL_VALUES } from './consts';
import { useSignInSubmitHandler } from './hooks';
import { SignInFormValues } from './types';
import useUserContext from '../../../hooks/useUserContext';
import useLanguageContext from '../../../hooks/useLanguageContext';
import SupportedLanguages from '../../../setup/intl/enums';
import {
  SignInButton,
  StyledSignInBrand,
  StyledSignInBrandDesc,
  StyledSignInBrandEyebrow,
  StyledSignInBrandFooter,
  StyledSignInBrandFeature,
  StyledSignInBrandFeatures,
  StyledSignInBrandHeading,
  StyledSignInBrandPitch,
  StyledSignInBrandTop,
  StyledSignInEyebrow,
  StyledSignInFields,
  StyledSignInForgot,
  StyledSignInFormBox,
  StyledSignInFormPane,
  StyledSignInHeading,
  StyledSignInLangButton,
  StyledSignInLangSwitch,
  StyledSignInRoot,
  StyledSignInSubtext,
} from './styles';

const BRAND_FEATURES = ['login.feature1', 'login.feature2', 'login.feature3'];

const SignInPage: React.FC = () => {
  const intl = useIntl();
  const { isUserLoading } = useUserContext();
  const { language, setLanguage } = useLanguageContext();
  const signInSubmitHandler = useSignInSubmitHandler();

  return (
    <>
      <Seo title={intl.formatMessage({ id: 'login.signIn', defaultMessage: 'Sign In' })} />
      <StyledSignInRoot>

        {/* ── Left brand pane ── */}
        <StyledSignInBrand>
          {/* Grid pattern + ISO box glyphs */}
          <svg
            viewBox="0 0 800 800"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.35, pointerEvents: 'none' }}
            aria-hidden="true"
          >
            <defs>
              <pattern id="si-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(197,232,97,.06)" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#si-grid)" />
            <g transform="translate(60,80)" fill="none" stroke="rgba(197,232,97,.5)" strokeWidth="1.5" strokeLinejoin="round">
              <path d="M0,40 L80,0 L160,40 L80,80 Z" />
              <path d="M0,40 L0,140 L80,180 L80,80" />
              <path d="M160,40 L160,140 L80,180" />
            </g>
            <g transform="translate(560,560)" fill="none" stroke="rgba(118,177,89,.45)" strokeWidth="1.5" strokeLinejoin="round">
              <path d="M0,30 L60,0 L120,30 L60,60 Z" />
              <path d="M0,30 L0,100 L60,130 L60,60" />
              <path d="M120,30 L120,100 L60,130" />
            </g>
          </svg>

          <StyledSignInBrandTop>
            <SandoogLogo size={48} withText light />
          </StyledSignInBrandTop>

          <StyledSignInBrandPitch>
            <StyledSignInBrandEyebrow>
              <FormattedMessage id='login.brandEyebrow' defaultMessage='Fulfillment platform for Iraq' />
            </StyledSignInBrandEyebrow>
            <StyledSignInBrandHeading>
              <FormattedMessage id='login.brandHeadingLine1' defaultMessage='Every order,' /><br />
              <FormattedMessage id='login.brandHeadingLine2' defaultMessage='every entity,' /><br />
              <FormattedMessage id='login.brandHeadingLine3' defaultMessage='one box.' />
            </StyledSignInBrandHeading>
            <StyledSignInBrandDesc>
              <FormattedMessage
                id='login.brandDescription'
                defaultMessage='Sandoog Center is where T store, MCA and Unimart teams run their orders, warehouse and payouts — together.'
              />
            </StyledSignInBrandDesc>
            <StyledSignInBrandFeatures>
              {BRAND_FEATURES.map((id) => (
                <StyledSignInBrandFeature key={id}>
                  <FormattedMessage id={id} />
                </StyledSignInBrandFeature>
              ))}
            </StyledSignInBrandFeatures>
          </StyledSignInBrandPitch>

          <StyledSignInBrandFooter>
            <span>
              <FormattedMessage
                id='login.copyright'
                defaultMessage='© {year} Sandoog'
                values={{ year: new Date().getFullYear() }}
              />
            </span>
            <Link to={routes.STATUS}><FormattedMessage id='login.footerStatus' defaultMessage='Status' /></Link>
            <Link to={routes.PRIVACY}><FormattedMessage id='login.footerPrivacy' defaultMessage='Privacy' /></Link>
            <Link to={routes.TERMS}><FormattedMessage id='login.footerTerms' defaultMessage='Terms' /></Link>
          </StyledSignInBrandFooter>
        </StyledSignInBrand>

        {/* ── Right form pane ── */}
        <StyledSignInFormPane>
          <StyledSignInLangSwitch>
            <StyledSignInLangButton
              type='button'
              active={language === SupportedLanguages.English}
              onClick={() => setLanguage(SupportedLanguages.English)}
            >
              English
            </StyledSignInLangButton>
            <StyledSignInLangButton
              type='button'
              active={language === SupportedLanguages.Arabic}
              onClick={() => setLanguage(SupportedLanguages.Arabic)}
            >
              العربية
            </StyledSignInLangButton>
          </StyledSignInLangSwitch>
          <StyledSignInFormBox>
            <StyledSignInEyebrow>
              <FormattedMessage id='login.welcomeBack' defaultMessage='Welcome back' />
            </StyledSignInEyebrow>
            <StyledSignInHeading>
              <FormattedMessage id='login.signIn' defaultMessage='Sign in to your workspace' />
            </StyledSignInHeading>
            <StyledSignInSubtext>
              <FormattedMessage id='login.newHere' defaultMessage='New here?' />{' '}
              <Link to={routes.SIGN_UP}>
                <FormattedMessage id='login.signUp' defaultMessage='Create an account' />
              </Link>
            </StyledSignInSubtext>

            <Form<SignInFormValues>
              onSubmit={signInSubmitHandler}
              initialValues={SIGN_IN_FORM_INITIAL_VALUES}
            >
              {({ handleSubmit, submitting, submitError }) => (
                <form onSubmit={handleSubmit}>
                  {submitError && (
                    <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 'var(--r-md)', background: 'var(--danger-soft)', color: 'var(--danger-ink)', fontSize: 'var(--t-sm)' }}>
                      {submitError}
                    </div>
                  )}
                  <StyledSignInFields>
                    <FormInput
                      disabled={submitting || isUserLoading}
                      label={intl.formatMessage({
                        id: 'forms.fields.emailAddress',
                        defaultMessage: 'Email Address',
                      })}
                      type='email'
                      name='email'
                    />
                    <FormInput
                      disabled={submitting || isUserLoading}
                      label={intl.formatMessage({
                        id: 'forms.fields.password',
                        defaultMessage: 'Password',
                      })}
                      type='password'
                      name='password'
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <StyledSignInForgot as={Link} to={routes.RESET_PASSWORD}>
                        <FormattedMessage id='login.forgotPassword' defaultMessage='Forgot password?' />
                      </StyledSignInForgot>
                    </div>
                    <SignInButton
                      type='submit'
                      kind={ButtonKind.Primary}
                      size={ButtonSize.Big}
                      disabled={submitting || isUserLoading}
                    >
                      <FormattedMessage id='login.signIn' defaultMessage='Sign in' />
                    </SignInButton>
                  </StyledSignInFields>
                </form>
              )}
            </Form>

          </StyledSignInFormBox>
        </StyledSignInFormPane>

      </StyledSignInRoot>
    </>
  );
};

export default SignInPage;
